import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Calendar, Plus, Check, X, Trash2, Clock, User } from 'lucide-react';
import { timeOff as timeOffApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import TimeOffRequestForm from '../components/TimeOffRequestForm';
import { format } from 'date-fns';
import { formatTo12Hour } from '../utils/timeFormat';

const TimeOffManagement = () => {
  const { isAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadRequests = async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await timeOffApi.getAll(params);
      
      // Filter out expired time off (past end date) unless viewing 'all'
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const filteredRequests = response.data.filter(request => {
        const endDate = new Date(request.end_date);
        endDate.setHours(0, 0, 0, 0);
        
        // Show all if filter is 'all', otherwise hide expired
        return filter === 'all' || endDate >= today;
      });
      
      setRequests(filteredRequests);
    } catch (error) {
      console.error('Load requests error:', error);
      toast.error('Failed to load time off requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await timeOffApi.review(id, { status: 'approved' });
      toast.success('Time off request approved!');
      loadRequests();
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Failed to approve request');
    }
  };

  const handleReject = async (id) => {
    try {
      await timeOffApi.review(id, { status: 'rejected' });
      toast.success('Time off request rejected');
      loadRequests();
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Failed to reject request');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this request?')) {
      return;
    }

    try {
      await timeOffApi.delete(id);
      toast.success('Time off request deleted');
      loadRequests();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete request');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      rejected: 'bg-red-100 text-red-800 border-red-300',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDateRange = (request) => {
    // Parse dates as local time to avoid timezone offset issues
    // The date string "2026-01-30" should be displayed as Jan 30, not Jan 29
    const parseLocalDate = (dateStr) => {
      const datePart = String(dateStr).split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    
    const start = format(parseLocalDate(request.start_date), 'MMM d, yyyy');
    const end = format(parseLocalDate(request.end_date), 'MMM d, yyyy');
    
    if (start === end) {
      if (request.start_time) {
        return `${start} (${formatTo12Hour(request.start_time)} - ${formatTo12Hour(request.end_time)})`;
      }
      return start;
    }
    return `${start} - ${end}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Calendar className="text-primary-600" size={32} />
            <h1 className="text-3xl font-bold text-gray-900">Time Off Requests</h1>
          </div>
          <button
            onClick={() => setShowRequestForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={20} />
            <span>Request Time Off</span>
          </button>
        </div>

        <div className="card mb-6">
          <div className="flex space-x-2">
            {['all', 'pending', 'approved', 'rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === status
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {requests.length === 0 ? (
            <div className="card text-center py-12">
              <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600 text-lg">No time off requests found</p>
            </div>
          ) : (
            requests.map((request) => (
              <div key={request.id} className="card hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {isAdmin && (
                      <div className="flex items-center space-x-2 mb-2">
                        <User size={16} className="text-gray-500" />
                        <span className="font-semibold text-gray-900">
                          {request.groomer_name}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center space-x-2 mb-2">
                      <Calendar size={16} className="text-gray-500" />
                      <span className="text-gray-700">{formatDateRange(request)}</span>
                      <span className="text-xs text-gray-500">
                        ({request.request_type})
                      </span>
                    </div>

                    <div className="flex items-center space-x-2 mb-2">
                      <Clock size={16} className="text-gray-500" />
                      <span className="text-gray-600 text-sm">
                        {request.total_days} day{request.total_days > 1 ? 's' : ''}
                      </span>
                    </div>

                    {request.reason && (
                      <p className="text-gray-600 text-sm mt-2 italic">
                        "{request.reason}"
                      </p>
                    )}

                    {request.status !== 'pending' && request.reviewed_by_name && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500">
                          {request.status === 'approved' ? 'Approved' : 'Rejected'} by{' '}
                          {request.reviewed_by_name} on{' '}
                          {format(new Date(request.reviewed_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end space-y-2 ml-4">
                    {getStatusBadge(request.status)}

                    {isAdmin && request.status === 'pending' && (
                      <div className="flex space-x-2 mt-2">
                        <button
                          onClick={() => handleApprove(request.id)}
                          className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                          title="Approve"
                        >
                          <Check size={18} />
                        </button>
                        <button
                          onClick={() => handleReject(request.id)}
                          className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                          title="Reject"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    )}

                    <button
                      onClick={() => handleDelete(request.id)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showRequestForm && (
        <TimeOffRequestForm
          onClose={() => setShowRequestForm(false)}
          onSuccess={loadRequests}
        />
      )}
    </div>
  );
};

export default TimeOffManagement;
